export type Action =
  | 'left'
  | 'right'
  | 'up'
  | 'down'
  | 'jump'
  | 'dive'
  | 'pause'
  | 'find'
  | 'insight'
  | 'respawn';

const KEYS: Record<string, Action> = {
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  Space: 'jump',
  KeyZ: 'jump',
  KeyK: 'jump',
  KeyX: 'dive',
  KeyE: 'dive',
  KeyJ: 'dive',
  Enter: 'dive',
  NumpadEnter: 'dive',
  Escape: 'pause',
  KeyP: 'pause',
  KeyF: 'find',
  Slash: 'find',
  KeyC: 'insight',
  KeyR: 'respawn',
};

/** Standard-mapping gamepad buttons. */
const PAD: [number, Action][] = [
  [0, 'jump'],
  [1, 'dive'],
  [2, 'dive'],
  [3, 'insight'],
  [8, 'find'],
  [9, 'pause'],
  [12, 'up'],
  [13, 'down'],
  [14, 'left'],
  [15, 'right'],
];

export class Input {
  readonly held = new Set<Action>();
  /** Presses since the last frame (menu-level actions read these once per frame). */
  readonly pressed = new Set<Action>();
  /**
   * Presses not yet seen by a fixed simulation step. On high-refresh displays some frames run zero
   * steps, so gameplay must read these rather than the per-frame `pressed`.
   */
  readonly stepPressed = new Set<Action>();
  private keys = new Set<string>();
  private touch = new Set<Action>();
  private pad = new Set<Action>();
  private padPrev = new Set<Action>();
  lastDevice: 'keyboard' | 'gamepad' | 'touch' = 'keyboard';

  constructor(private shouldCapture: () => boolean) {
    window.addEventListener('keydown', (e) => {
      if (isTyping(e.target)) return;
      const a = KEYS[e.code];
      if (!a || !this.shouldCapture()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      this.lastDevice = 'keyboard';
      if (!this.keys.has(e.code)) this.pressed.add(a);
      this.keys.add(e.code);
    });
    window.addEventListener('keyup', (e) => {
      this.keys.delete(e.code);
    });
    window.addEventListener('blur', () => this.clear());
    document.addEventListener('visibilitychange', () => this.clear());
  }

  clear(): void {
    this.keys.clear();
    this.touch.clear();
    this.held.clear();
    this.dropPresses();
  }

  /** Forget presses nobody has acted on yet (e.g. the button press that just closed a menu). */
  dropPresses(): void {
    this.pressed.clear();
    this.stepPressed.clear();
  }

  /** Call after a frame that ran at least one simulation step. */
  consumeStep(): void {
    this.stepPressed.clear();
  }

  setTouch(a: Action, down: boolean): void {
    this.lastDevice = 'touch';
    if (down) {
      if (!this.touch.has(a)) this.pressed.add(a);
      this.touch.add(a);
    } else this.touch.delete(a);
  }

  /** Call once per animation frame before reading `held`/`pressed`. */
  poll(): void {
    this.pad.clear();
    const pads = navigator.getGamepads?.() ?? [];
    for (const gp of pads) {
      if (!gp || !gp.connected) continue;
      for (const [i, a] of PAD) if (gp.buttons[i]?.pressed) this.pad.add(a);
      const ax = gp.axes[0] ?? 0;
      const ay = gp.axes[1] ?? 0;
      if (ax < -0.4) this.pad.add('left');
      if (ax > 0.4) this.pad.add('right');
      if (ay < -0.55) this.pad.add('up');
      if (ay > 0.55) this.pad.add('down');
    }
    for (const a of this.pad)
      if (!this.padPrev.has(a)) {
        this.pressed.add(a);
        this.lastDevice = 'gamepad';
      }
    this.padPrev = new Set(this.pad);
    this.held.clear();
    for (const code of this.keys) {
      const a = KEYS[code];
      if (a) this.held.add(a);
    }
    for (const a of this.touch) this.held.add(a);
    for (const a of this.pad) this.held.add(a);
    for (const a of this.pressed) this.stepPressed.add(a);
  }

  endFrame(): void {
    this.pressed.clear();
  }
}

export function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}
